/**
 * Team directory: search, filters, create-team entry, and navigation to `TeamDetails`.
 *
 * Where: `/teams`.
 * Uses: `teamApi`, URL search params, debounced search, `TeamCard` grid.
 * Behavior: persists filter state in query string; responsive grid/list.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  CorporateFare as CorporateFareIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import { chatApi } from '@/api/chat';
import { useAuth } from '@/hooks/useAuth';
import { TeamCard } from '@/components/team/TeamCard';
import { TeamCreateModal } from '@/components/team';
import type { TeamResponse, TeamItem } from '@/types/team';
import type { InfiniteData } from '@tanstack/react-query';


interface TeamsProps {}

export const Teams: React.FC<TeamsProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Any authenticated user can create a team
  const canCreateTeam = !!currentUser;
  
  // Initialize filters from URL params
  const [nameSearch, setNameSearch] = useState(() => searchParams.get('name') || '');
  const [memberFilters, setMemberFilters] = useState<string[]>(() => {
    const membersParam = searchParams.get('members');
    return membersParam ? membersParam.split(',').filter(Boolean) : [];
  });
  const [ownerFilter, setOwnerFilter] = useState<string>(() => searchParams.get('owner') || '');
  
  // Debounced name search for URL updates
  const nameSearchDebounced = useDebouncedValue(nameSearch, 500);
  
  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Fetch all users for member and owner filters - fetch all without search, filter client-side
  const {
    data: membersPages,
    fetchNextPage: fetchNextMembersPage,
    hasNextPage: hasNextMembersPage,
    isFetchingNextPage: isFetchingNextMembersPage,
    isLoading: isLoadingMembers,
  } = useInfiniteQuery({
    queryKey: ['team-members-all'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getProjectOwners({ page_token: pageParam, max_results: 50 }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
  });

  const {
    data: ownersPages,
    fetchNextPage: fetchNextOwnersPage,
    hasNextPage: hasNextOwnersPage,
    isFetchingNextPage: isFetchingNextOwnersPage,
    isLoading: isLoadingOwners,
  } = useInfiniteQuery({
    queryKey: ['team-owners-all'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getProjectOwners({ page_token: pageParam, max_results: 50 }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
  });

  // Flatten all members and owners from all pages
  const allMembers = membersPages?.pages.flatMap((p) => p.items) ?? [];
  const allOwners = ownersPages?.pages.flatMap((p) => p.items) ?? [];

  // Get selected members from all fetched members
  const selectedMembers = useMemo(() => {
    return allMembers.filter((m) => memberFilters.includes(m.uuid));
  }, [allMembers, memberFilters]);

  // Auto-fetch all pages of members
  useEffect(() => {
    if (hasNextMembersPage && !isFetchingNextMembersPage && !isLoadingMembers) {
      fetchNextMembersPage();
    }
  }, [hasNextMembersPage, isFetchingNextMembersPage, isLoadingMembers, fetchNextMembersPage]);

  // Auto-fetch all pages of owners
  useEffect(() => {
    if (hasNextOwnersPage && !isFetchingNextOwnersPage && !isLoadingOwners) {
      fetchNextOwnersPage();
    }
  }, [hasNextOwnersPage, isFetchingNextOwnersPage, isLoadingOwners, fetchNextOwnersPage]);

  // Update URL params when filters change
  const updateURLParams = useCallback((updates: {
    name?: string;
    members?: string[];
    owner?: string;
  }) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (updates.name !== undefined) {
      if (updates.name) {
        newSearchParams.set('name', updates.name);
      } else {
        newSearchParams.delete('name');
      }
    }
    
    if (updates.members !== undefined) {
      if (updates.members.length > 0) {
        newSearchParams.set('members', updates.members.join(','));
      } else {
        newSearchParams.delete('members');
      }
    }
    
    if (updates.owner !== undefined) {
      if (updates.owner) {
        newSearchParams.set('owner', updates.owner);
      } else {
        newSearchParams.delete('owner');
      }
    }
    
    setSearchParams(newSearchParams, { replace: true });
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Update URL when debounced name search changes
  useEffect(() => {
    updateURLParams({ name: nameSearchDebounced });
  }, [nameSearchDebounced, updateURLParams]);

  // Update URL when other filters change
  useEffect(() => {
    updateURLParams({ members: memberFilters });
  }, [memberFilters, updateURLParams]);

  useEffect(() => {
    updateURLParams({ owner: ownerFilter });
  }, [ownerFilter, updateURLParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return;
    }

    const urlName = searchParams.get('name') || '';
    const urlMembers = searchParams.get('members') ? searchParams.get('members')!.split(',').filter(Boolean) : [];
    const urlOwner = searchParams.get('owner') || '';

    if (urlName !== nameSearch) {
      setNameSearch(urlName);
    }
    if (JSON.stringify(urlMembers) !== JSON.stringify(memberFilters)) {
      setMemberFilters(urlMembers);
    }
    if (urlOwner !== ownerFilter) {
      setOwnerFilter(urlOwner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch teams with infinite scroll
  // Include user key in query key to ensure proper data refresh on impersonation
  const {
    data: teamsPages,
    isLoading: isLoadingTeams,
    error: teamsError,
    fetchNextPage: fetchNextTeamsPage,
    hasNextPage: hasNextTeamsPage,
    isFetchingNextPage: isFetchingNextTeamsPage,
  } = useInfiniteQuery<
    TeamResponse,
    Error,
    InfiniteData<TeamResponse, string | undefined>,
    [string, string | undefined, string, string, string[]],
    string | undefined
  >({
    queryKey: ['teams', currentUser?.key, nameSearchDebounced, ownerFilter, memberFilters] as [string, string | undefined, string, string, string[]],
    queryFn: ({ pageParam }) =>
      teamApi.getTeams({
        page_token: pageParam,
        max_results: 10,
        add_owner_info: true,
        add_roles: true,
        sort_field: 'name',
        sort_direction: 'asc',
        search: nameSearchDebounced || undefined,
        owner_uuid: ownerFilter || undefined,
        all_member_uuids: memberFilters.length > 0 ? memberFilters.join(',') : undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined,
  });

  // Flatten teams from all pages
  const teams: TeamItem[] = teamsPages?.pages.flatMap((page: TeamResponse) => page.output.items) ?? [];

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextTeamsPage && !isFetchingNextTeamsPage) {
          fetchNextTeamsPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextTeamsPage, isFetchingNextTeamsPage, fetchNextTeamsPage]);



  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Teams
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your teams and track their performance.
            </Typography>
          </Box>
          {canCreateTeam && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateOpen(true)}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              {isMobile ? 'New' : 'New Team'}
            </Button>
          )}
        </Box>

        {/* Query Bar */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 2 }, 
          p: 2, 
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}>
          {/* Search Input - Full width on mobile */}
          <TextField
            placeholder="Search teams by name..."
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          {/* Active Filters Chips - Stack on mobile */}
          <Stack 
            direction="row" 
            spacing={0}
            sx={{ 
              flex: { xs: 'none', sm: 1 }, 
              minWidth: 0,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-start' },
              '& .MuiChip-root': {
                mb: { xs: 1, sm: 0 }
              }
            }}
          >
            {nameSearch && (
              <Chip
                label={`Name: "${nameSearch}"`}
                size="small"
                onDelete={() => setNameSearch('')}
                color="secondary"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {memberFilters.length > 0 && (
              <Chip
                label={`Members: ${memberFilters.length}`}
                size="small"
                onDelete={() => setMemberFilters([])}
                color="primary"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            {ownerFilter && (
              <Chip
                label={`Owner: ${allOwners.find((o) => o.uuid === ownerFilter)?.name || ownerFilter}`}
                size="small"
                onDelete={() => setOwnerFilter('')}
                color="info"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
          </Stack>

          {/* Filter Controls - Stack on mobile */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* Members Filter (Multi-select) */}
            <Autocomplete
              multiple
              size="small"
              options={allMembers}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              value={selectedMembers}
              onChange={(_, newValue) => {
                setMemberFilters(newValue.map((v) => v.uuid));
              }}
              loading={isLoadingMembers || isFetchingNextMembersPage}
              filterOptions={(options, state) => {
                // Filter based on current input value
                if (!state.inputValue) {
                  return options;
                }
                const searchLower = state.inputValue.toLowerCase();
                return options.filter((option) =>
                  option.name.toLowerCase().includes(searchLower)
                );
              }}
              filterSelectedOptions
              ListboxProps={{
                onScroll: (event: React.SyntheticEvent) => {
                  const listboxNode = event.currentTarget;
                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                    hasNextMembersPage &&
                    !isFetchingNextMembersPage
                  ) {
                    fetchNextMembersPage();
                  }
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Members contains..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <FilterIcon fontSize="small" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    minWidth: { xs: '100%', sm: 250 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: 250 },
              }}
            />

            {/* Owner Filter (Single select) */}
            <Autocomplete
              size="small"
              options={allOwners}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              value={allOwners.find((o) => o.uuid === ownerFilter) || null}
              onChange={(_, newValue) => setOwnerFilter(newValue?.uuid || '')}
              loading={isLoadingOwners || isFetchingNextOwnersPage}
              filterOptions={(options, state) => {
                // Filter options based on search input
                if (!state.inputValue) {
                  return options;
                }
                const searchLower = state.inputValue.toLowerCase();
                return options.filter((option) =>
                  option.name.toLowerCase().includes(searchLower)
                );
              }}
              ListboxProps={{
                onScroll: (event: React.SyntheticEvent) => {
                  const listboxNode = event.currentTarget;
                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                    hasNextOwnersPage &&
                    !isFetchingNextOwnersPage
                  ) {
                    fetchNextOwnersPage();
                  }
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Owner..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <FilterIcon fontSize="small" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: 200 },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {isLoadingTeams ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : teamsError ? (
          <Alert severity="error">
            Failed to load teams. Please try again.
          </Alert>
        ) : teams.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CorporateFareIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No teams yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first team to get started with managing experiments and leads.
            </Typography>
            {canCreateTeam && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateOpen(true)}
              >
                Create Team
              </Button>
            )}
          </Box>
        ) : (
          <>
            {/* Team Cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {teams.map((team: TeamItem) => (
                  <TeamCard
                    key={team.key}
                    team={team}
                  isMobile={isMobile}
                  />
                ))}
              </Box>

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextTeamsPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Load more trigger */}
            <div ref={loadMoreRef} style={{ height: 20 }} />
          </>
        )}
      </Box>

      <TeamCreateModal open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </Box>
  );
};
